import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import { chargebeeUtils } from '../utils';
import { ESdkEvents } from '../../../../contracts';
import { sendFEEvent } from '../../../../eventUtil';
import { useAPI } from '../../../../services/api.service';
import { useCheckout } from '../../../../hooks/use-checkout';
import { useSubscriptionPolling } from './use-subscription-polling';
import { usePaymentState } from '../../../Checkout/hooks/use-payment-state';
import { ChargebeeComponent, ChargebeeInstance } from '../../../../types/chargebee';
import { useShowTaxCalculationFields } from '../../../Checkout/hooks/use-show-tax-calculation-fields';
import { useAddressElementRef } from '../../../Checkout/components/AddressElement/hooks/use-address-element-ref';

export function useChargebeePaymentElement() {
  const ref = useRef<HTMLDivElement>(null);
  const { API } = useAPI();
  const [showPayButton, setShowPayButton] = useState(false);
  const [paymentComponent, setPaymentComponent] = useState<ChargebeeComponent | null>(null);
  const setIsPaymentElementLoading = usePaymentState((state) => state.setIsPaymentElementLoading);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const { checkoutData, subscriptionData, getEventParams } = useCheckout();
  const { AddressElementRef } = useAddressElementRef();
  const { useTaxCalculationField } = useShowTaxCalculationFields();
  const { startSubscriptionPolling } = useSubscriptionPolling();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();
  const email = checkoutData?.customer?.email;
  const plan = subscriptionData?.plan;
  const paymentIntent = subscriptionData?.paymentIntent;
  const chargebeeConfig = checkoutData?.pks?.chargebee;

  useEffect(() => {
    async function initializeChargebee() {
      const chargebee: ChargebeeInstance = window.Chargebee.init({
        site: chargebeeConfig?.siteId || '',
        publishableKey: chargebeeConfig?.publicKey || '',
      });

      const components = chargebee.components(chargebeeUtils.getComponentsOptions());

      const paymentComponent = components.create(
        'payment',
        chargebeeUtils.getPaymentComponentOptions(paymentIntent, email),
        {
          onError: (error: any) => {
            handleError(error.message);
          },
          onSuccess: async (paymentIntent: any, extra: any) => {
            console.log('onSuccess', { paymentIntent, extra });
            try {
              const finalizeSubscriptionResponse = await API.finalizeSubscriptionPayment({
                params: {
                  planId: plan?._id,
                },
                customer: {
                  id: checkoutData?.customer?.id,
                  email: checkoutData?.customer?.email,
                },
              });
              if (finalizeSubscriptionResponse.status === 'success') {
                startSubscriptionPolling(finalizeSubscriptionResponse.subscriptionId);
                sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
                  ...getEventParams(),
                });
              }
              console.log('finalizeSubscriptionResponse', finalizeSubscriptionResponse);
            } catch (error: any) {
              const errResponse = error?.response?.data;
              showBoundary(t('somethingWentWrong'));
            }
          },
          onPaymentMethodChange: (paymentMethod: string) => {
            setShowPayButton(paymentMethod === 'card');
            setIsPaymentElementLoading(false);
          },
          onButtonClick: () => {
            AddressElementRef.current?.validate();
            const isAddressValid =
              !useTaxCalculationField || Boolean(AddressElementRef.current?.isValid());
            if (isAddressValid) {
              return Promise.resolve();
            }
            return Promise.reject();
          },
        }
      );
      setPaymentComponent(paymentComponent);

      paymentComponent.mount(ref.current);
    }
    if (paymentIntent) {
      initializeChargebee();
    }
  }, [paymentIntent, plan, chargebeeConfig]);

  async function handlePaymentButtonClick() {
    const isValid = await paymentComponent?.validate();
    AddressElementRef.current?.validate();
    const isAddressValid = !useTaxCalculationField || Boolean(AddressElementRef.current?.isValid());

    console.log('isValid', isValid);
    if (isValid && isAddressValid) {
      setIsPaymentLoading(true);
      const confirmResult = await paymentComponent?.confirm();
      console.log('confirmResult', confirmResult);
    }
  }

  function handleError(error: any) {
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
    });
    setIsPaymentLoading(false);
    setErrorMessage(error);
  }

  return {
    ref,
    showPayButton,
    paymentComponent,
    AddressElementRef,
    setIsPaymentLoading,
    handlePaymentButtonClick,
    setIsPaymentElementLoading,
  };
}
